import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Container, FormControl, Grid, MenuItem, Select } from "@mui/material";

import logo from "../../../assets/safe_logo.png";
import RoundButton from "../../../components/RoundButton";
import { phoneValidationRequest } from "../../../redux/signup/action";
import codeMessage from "../../../services/apiManager/httpMessages";
import CustomLoader from "../../../components/CustomLoader";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import operatorAccountScheme from "../../../validations/operatorAccountScheme";
import { updateOperatorAccountDetails } from "../../../redux/operator/action";
import { useResponsive } from "../../../hooks/useResponsive";
import background_image from "../../../assets/png/background_image.png";
import storePersist, { localConstant } from "../../../services/localStorage";
import LableComponent from "../../../components/LableComponent";
import { PasswordChecklist } from "../../signup";
import { useTranslation } from "react-i18next";
import { ReactComponent as Frame } from "../../../assets/svg/Frame.svg";
import { isMobileDeviceCheck } from "../../../utils";
import { COUNTRY_CODE, DEFAULT_COUNTRY_CODE } from "../../../utils/utils";

const OperatorAccountDetails = () => {
  const { operatorAccountDetails } = useSelector(
    (state) => state.operatorReducer,
  );

  const { inviteDriverDetails } = useSelector(
    (state) => state.verifyUserReducer,
  );

  const { isLoading } = useSelector((state) => state.signUpReducer);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const smDown = useResponsive("down", "sm");

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      email: inviteDriverDetails.email,
      ...operatorAccountDetails,
      countryCode: DEFAULT_COUNTRY_CODE,
    },
    resolver: yupResolver(operatorAccountScheme),
  });

  const submitForm = (values) => {
    const newValues = {
      ...values,
    };

    delete newValues["phoneNumber"];
    delete newValues["countryCode"];
    newValues["phone"] = {
      phoneNumber: values?.phoneNumber,
      intlDialCd: values?.countryCode,
    };
    if (values?.phoneNumber?.length > 8)
      dispatch(
        phoneValidationRequest(
          values?.phoneNumber,
          values?.countryCode,
          {},
          (response) => {
            if (response?.message === codeMessage[400]) {
              setError("phoneNumber", {
                type: "manual",
                message: "Invalid phone number",
              });
            } else {
              if (inviteDriverDetails.source === "G2") {
                //we need to finish operator signup here since invitation came from G2 portal
              } else {
                dispatch(updateOperatorAccountDetails(newValues));
                storePersist.set(
                  localConstant.OPERATOR_SIGN_UP_DETAILS,
                  newValues,
                );
                navigate("/operatorAddressDetails");
              }
            }
          },
        ),
      );
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const { i18n } = useTranslation();

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  const [language, setLanguage] = useState(i18n?.language?.split("-")?.[0]);

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
    changeLanguage(e.target.value);
    storePersist.set(localConstant.LANGUAGE, e.target.value);
  };

  const password = useWatch({ control, name: "password" });

  return (
    <Container
      disableGutters
      maxWidth={false}
      style={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: "#80BCFF4F",
        backgroundImage: `url(${background_image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100%",
      }}
    >
      <Box
        maxWidth="xs"
        sx={{
          width: 1,
          flexGrow: 1,
          minHeight: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: 1,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            flexDirection: ["column", "column", "row"],
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 1, width: 1, padding: 0, gap: 5 }}
          >
            <img src={logo} alt="Your Alt Text" />
            <Card
              sx={{
                p: 3,
                width: 1,
                height: 1,
                maxWidth: 600,
                maxHeight: smDown ? "auto" : 550,
              }}
              style={{
                overflow: "auto",
              }}
              className="form_container"
            >
              <FormControl
                sx={{
                  m: 0,
                  minWidth: 120,
                  position: "absolute",
                  top: "1%",
                  right: "1%",
                }}
                size="small"
              >
                <Select
                  labelId="language_dropdown_label"
                  id="language_dropdown"
                  value={language}
                  label=""
                  onChange={handleLanguageChange}
                  sx={{
                    color: "grey",
                    borderColor: "grey",
                    border: "none",
                    "& .MuiSvgIcon-root": { color: "grey" },
                    "&:before": { borderColor: "grey" },
                    "&:after": { borderColor: "grey" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "grey",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid lightgrey !important",
                    },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* This is where you insert your icon or Frame component if it's not an icon */}
                      <Frame />
                      {/* Display the selected language text */}
                      {selected === "en"
                        ? "English"
                        : selected === "es"
                          ? "Español"
                          : "Русский"}
                    </Box>
                  )}
                >
                  <MenuItem value={"en"}>English</MenuItem>
                  <MenuItem value={"es"}>Español</MenuItem>
                  <MenuItem value={"ru"}>Русский</MenuItem>
                </Select>
              </FormControl>
              <form onSubmit={handleSubmit((data) => submitForm(data))}>
                <Stack spacing={2}>
                  <Typography variant="titleText" color="primary.main">
                    Create your account
                  </Typography>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={<LableComponent customLable="Email ID" />}
                        name="email"
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={inviteDriverDetails?.email}
                      />
                    )}
                  ></Controller>
                  <Stack spacing={1} direction={smDown ? "column" : "row"}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="First Name"
                          label={<LableComponent customLable="First Name" />}
                          name="firstName"
                          type="text"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                        />
                      )}
                    ></Controller>
                    <Controller
                      name="middleName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Middle Name"
                          label={
                            <LableComponent
                              isNotRequired
                              customLable="Middle Name"
                            />
                          }
                          name="middleName"
                          type="text"
                        />
                      )}
                    ></Controller>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Last Name"
                          label={<LableComponent customLable="Last Name" />}
                          name="lastName"
                          type="text"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                        />
                      )}
                    ></Controller>
                  </Stack>
                  <Grid
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                  >
                    <Grid>
                      <Controller
                        name="countryCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            renderValue={(value) =>
                              value?.length
                                ? Array.isArray(value)
                                  ? value.join(", ")
                                  : value
                                : "Country Code"
                            }
                            sx={{
                              width: isMobileDeviceCheck() ? 80 : 100,
                            }}
                            labelId="demo-simple-select-autowidth-label"
                            id="demo-simple-select-autowidth"
                            autoWidth
                            label="Country Code"
                            {...field}
                          >
                            {Object.keys(COUNTRY_CODE).map((item) => {
                              return (
                                <MenuItem value={item}>
                                  {COUNTRY_CODE[item]}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      ></Controller>
                      <p style={{ color: "#d32f2f", fontSize: "12px" }}>
                        {errors.countryCode?.message}
                      </p>
                    </Grid>

                    <Grid width={"70%"}>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            {...field}
                            name="phoneNumber"
                            type="tel"
                            label={
                              <LableComponent customLable="Phone Number" />
                            }
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                          />
                        )}
                      ></Controller>
                    </Grid>
                  </Grid>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        name="password"
                        label={<LableComponent customLable="Set Password" />}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        type={showPassword ? "text" : "password"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  ></Controller>
                  <PasswordChecklist password={password} />
                </Stack>
                <RoundButton
                  sx={{ mt: 5, mb: 2 }}
                  size="large"
                  fullWidth
                  variant="contained"
                  type="submit"
                  color="primary"
                  rounded
                >
                  <Typography variant="buttonText" color="common.buttonText">
                    Continue
                  </Typography>
                </RoundButton>
              </form>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
              ></Stack>
            </Card>
          </Stack>
        </Box>
      </Box>
      {isLoading && <CustomLoader />}
    </Container>
  );
};

export default OperatorAccountDetails;
